/**
 * New case (docs/ux/intake.md): the referral arrives and the case starts at 1.
 * A dialog rather than a page because the field set is short and the admin is
 * mid-flow on the board, which a page would hide.
 *
 * Prefill is a button, never a mode: it writes visible values into visible
 * fields and saves nothing, so with an audience watching the admin can see
 * exactly what is about to be created and edit any of it first.
 */

import { ALL_REPORT_TYPES, reportTypeLabel, statusLabel, type ReportTypeKind } from '@beam/core/status';
import { useEffect, useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ApiError, reasonOf } from '@/app/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDoctors, useReferrers } from '../registry/useRegistry';
import { useCreateCase, useDemoPrefill, useLetterFromDemo, type NewCaseInput } from './useIntake';

type Field = keyof NewCaseInput;

const EMPTY: NewCaseInput = { matterRef: '', patientAsOnLoi: '', referrerId: '', doctorSlug: '', reportType: 'ime', dueDate: '' };

/** Inline, on the field, because the dialog is on screen and the field is where the reader is looking. */
function validate(v: NewCaseInput): Partial<Record<Field, string>> {
  const out: Partial<Record<Field, string>> = {};
  if (!/^[A-Za-z]{2}-\d{4}-\d{3,6}$/.test(v.matterRef.trim())) out.matterRef = 'Use the practice format, for example WC-2026-09201.';
  if (v.patientAsOnLoi.trim().length === 0) out.patientAsOnLoi = 'The patient as the letter names them.';
  if (!v.referrerId) out.referrerId = 'Choose the referrer.';
  if (!v.doctorSlug) out.doctorSlug = 'Choose the doctor.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v.dueDate)) out.dueDate = 'Choose the date the report is due.';
  return out;
}

export function NewCaseDialog({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange: (open: boolean) => void; onCreated?: (ref: string) => void }) {
  const id = useId();
  const [value, setValue] = useState<NewCaseInput>(EMPTY);
  const [letter, setLetter] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [filled, setFilled] = useState<string | null>(null);
  const firstField = useRef<HTMLInputElement>(null);

  const referrers = useReferrers();
  const doctors = useDoctors();
  const prefill = useDemoPrefill();
  const create = useCreateCase();
  const attach = useLetterFromDemo(value.matterRef.trim().toUpperCase());

  // Reopening starts clean: nothing was saved, so nothing should persist.
  useEffect(() => {
    if (!open) return;
    setValue(EMPTY); setLetter(null); setErrors({}); setFailure(null); setFilled(null);
  }, [open]);

  const set = (field: Field, next: string) => {
    setValue((v) => ({ ...v, [field]: next }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  function fill() {
    const p = prefill.data;
    if (!p) return;
    setValue({ ...p.case });
    setLetter(p.case.letter);
    setErrors({});
    setFilled(p.case.letter ? `Filled, letter ${p.case.letter}` : 'Filled from the demo files');
  }

  async function submit() {
    const found = validate(value);
    setErrors(found);
    setFailure(null);
    if (Object.keys(found).length > 0) return;
    const ref = value.matterRef.trim().toUpperCase();
    try {
      await create.mutateAsync({ ...value, matterRef: ref, patientAsOnLoi: value.patientAsOnLoi.trim() });
      // The letter is a second call on purpose: a case that exists without its
      // letter is a normal state, so a failure here must not lose the case.
      if (letter) {
        try { await attach.mutateAsync(letter); } catch { toast.warning(`${ref} was created, but the letter did not attach. Attach it on the LOI tab.`); }
      }
      toast.success(`${ref} created at 1 ${statusLabel(1)}.`);
      onCreated?.(ref);
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setErrors({ matterRef: 'That reference is already in use.' });
      else setFailure(reasonOf(err) ?? 'The case could not be created.');
    }
  }

  const files = prefill.data?.files.filter((f) => f.pdf) ?? [];
  const busy = create.isPending || attach.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-slot="new-case">
        <DialogHeader>
          <DialogTitle>New case</DialogTitle>
          <DialogDescription>The case is created at 1 {statusLabel(1)}.</DialogDescription>
        </DialogHeader>

        {prefill.data ? (
          <div className="flex items-center gap-3">
            <Button type="button" variant="outline" onClick={fill} disabled={busy}>Fill from demo files</Button>
            <span className="text-[13px] text-beam-text-soft">{filled ?? `${prefill.data.files.length} ${prefill.data.files.length === 1 ? 'file' : 'files'} available`}</span>
          </div>
        ) : null}

        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor={`${id}-ref`}>Reference</Label>
            <Input ref={firstField} id={`${id}-ref`} value={value.matterRef} onChange={(e) => set('matterRef', e.target.value)}
              aria-invalid={Boolean(errors.matterRef)} aria-describedby={errors.matterRef ? `${id}-ref-error` : undefined} placeholder="WC-2026-09201" />
            {errors.matterRef ? <p id={`${id}-ref-error`} className="text-[13px] text-beam-error">{errors.matterRef}</p> : null}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor={`${id}-patient`}>Patient, as on the letter</Label>
            <Input id={`${id}-patient`} value={value.patientAsOnLoi} onChange={(e) => set('patientAsOnLoi', e.target.value)}
              aria-invalid={Boolean(errors.patientAsOnLoi)} aria-describedby={errors.patientAsOnLoi ? `${id}-patient-error` : undefined} placeholder="SURNAME Given" />
            {errors.patientAsOnLoi ? <p id={`${id}-patient-error`} className="text-[13px] text-beam-error">{errors.patientAsOnLoi}</p> : null}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor={`${id}-referrer`}>Referrer</Label>
            <Select value={value.referrerId} onValueChange={(v) => set('referrerId', v)}>
              <SelectTrigger id={`${id}-referrer`} aria-invalid={Boolean(errors.referrerId)}><SelectValue placeholder="Choose a referrer" /></SelectTrigger>
              <SelectContent>
                {(referrers.data ?? []).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}{r.kind === 'plaintiff' ? ' (plaintiff)' : ''}</SelectItem>)}
              </SelectContent>
            </Select>
            {errors.referrerId ? <p className="text-[13px] text-beam-error">{errors.referrerId}</p> : null}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor={`${id}-type`}>Report type</Label>
            <Select value={value.reportType} onValueChange={(v) => set('reportType', v as ReportTypeKind)}>
              <SelectTrigger id={`${id}-type`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {ALL_REPORT_TYPES.map((t) => <SelectItem key={t} value={t}>{reportTypeLabel(t, 'label')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor={`${id}-doctor`}>Doctor</Label>
            <Select value={value.doctorSlug} onValueChange={(v) => set('doctorSlug', v)}>
              <SelectTrigger id={`${id}-doctor`} aria-invalid={Boolean(errors.doctorSlug)}><SelectValue placeholder="Choose a doctor" /></SelectTrigger>
              <SelectContent>
                {(doctors.data ?? []).filter((d) => d.active).map((d) => <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {errors.doctorSlug ? <p className="text-[13px] text-beam-error">{errors.doctorSlug}</p> : null}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor={`${id}-due`}>Report due</Label>
            <Input id={`${id}-due`} type="date" value={value.dueDate} onChange={(e) => set('dueDate', e.target.value)}
              aria-invalid={Boolean(errors.dueDate)} aria-describedby={errors.dueDate ? `${id}-due-error` : undefined} />
            {errors.dueDate ? <p id={`${id}-due-error`} className="text-[13px] text-beam-error">{errors.dueDate}</p> : null}
          </div>

          {files.length > 0 ? (
            <fieldset className="grid gap-1.5">
              <legend className="text-[13px] font-medium">Letter</legend>
              <label className="flex items-center gap-2 text-[13px]">
                <input type="radio" name={`${id}-letter`} checked={letter === null} onChange={() => setLetter(null)} />
                <span>None yet, attach it on the LOI tab</span>
              </label>
              {files.map((f) => (
                <label key={f.name} className="flex items-center gap-2 text-[13px]">
                  <input type="radio" name={`${id}-letter`} checked={letter === f.name} onChange={() => setLetter(f.name)} />
                  <span className="truncate">{f.name}</span>
                  <span className="ml-auto shrink-0 text-beam-text-soft">{Math.round(f.bytes / 1024)} KB</span>
                </label>
              ))}
            </fieldset>
          ) : null}
        </div>

        {failure ? <p role="alert" className="text-[13px] text-beam-error">{failure}</p> : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button type="button" data-primary="true" onClick={() => void submit()} disabled={busy}>{busy ? 'Creating' : 'Create'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
